import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle, ArrowRight, Check, CheckCircle2, Clock3, Eye, FileEdit, RotateCcw, Send, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { ActionBar, BarButton } from '@generated/components/form/ActionBar';
import { PostdocDocument } from '@generated/components/form/postdoc/PostdocDocument';
import { PostdocEditor } from '@generated/components/form/postdoc/PostdocEditor';
import { PostdocReceipt } from '@generated/components/form/postdoc/PostdocReceipt';
import { TurnstileWidget } from '@generated/components/TurnstileWidget';
import { usePostdocForm } from '@generated/hooks/usePostdocForm';
import { generateUnifiedPostdocPdf } from '@generated/lib/postdoc/generatePdf';
import {
  createPostdocBursaryRequest,
  uploadPostdocAttachment,
} from '@generated/server/postdocBursary';

export const Route = createFileRoute('/_app/forms/postdoc-bursary')({
  component: PostdocBursaryPage,
});

type Mode = 'edit' | 'review' | 'submitted';

function dataUrlToFile(dataUrl: string, filename: string, mimeType = 'application/octet-stream'): File | null {
  try {
    const parts = dataUrl.split(',');
    const base64 = parts[1] || parts[0];
    if (!base64) return null;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mimeType });
  } catch (e) {
    console.warn('dataUrlToFile failed', e);
    return null;
  }
}

function PostdocBursaryPage() {
  const api = usePostdocForm();
  const documentRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<Mode>('edit');
  const [attemptedReview, setAttemptedReview] = useState(false);
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);

  const dirty = Boolean(
    api.values.idNumber ||
      api.values.firstName ||
      api.values.lastName ||
      api.values.bankName ||
      api.values.email ||
      api.values.signature ||
      api.values.isContinuingStudent,
  );

  const handleClearForm = () => {
    api.reset();
    api.clearDraftStorage();
    setReference('');
    setSubmitError(null);
    setAttemptedReview(false);
    setTurnstileToken(null);
    setShowResetDialog(false);
    toast.success('הטופס והטיוטה אופסו בהצלחה / Form cleared successfully');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (mode !== 'edit' || !dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, mode]);

  const handleReview = () => {
    setAttemptedReview(true);
    api.touchAll();
    if (!api.isValid) {
      toast.error('יש להשלים את כל שדות החובה לפני המעבר לסקירה / Please complete all required fields', {
        description: `נותרו ${api.missingItems.length} שדות חובה / ${api.missingItems.length} required fields remaining`,
      });
      if (api.missingItems[0]) {
        api.jumpTo(api.missingItems[0].id);
      }
      return;
    }
    setMode('review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    api.touchAll();
    if (!api.isValid) {
      toast.error('יש להשלים את כל שדות החובה לפני השליחה / Please complete all required fields before submitting');
      setMode('edit');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Generate multi-page signed PDF questionnaire + all appendix documents
      let signedPdfFile = null;
      if (documentRef.current) {
        try {
          const pdfFilename = `טופס_בקשה_חתום_${api.values.lastName}_${api.values.firstName}_${api.values.idNumber}.pdf`;
          signedPdfFile = await generateUnifiedPostdocPdf({
            containerElement: documentRef.current,
            values: api.values,
            filename: pdfFilename,
          });
        } catch (pdfGenErr) {
          console.error('[Postdoc Bursary] Unified PDF generation before submit failed:', pdfGenErr);
        }
      }

      // 2. Submit clean, lightweight form metadata to create/update board item
      const res = await createPostdocBursaryRequest({
        data: {
          isContinuingStudent: api.values.isContinuingStudent,
          continuingDeclarationConfirmed: api.values.continuingDeclarationConfirmed,
          continuingItemId: api.values.continuingItemId,
          idType: api.values.idType,
          idNumber: api.values.idNumber,
          dateOfBirth: api.values.dateOfBirth,
          gender: api.values.gender,
          firstName: api.values.firstName,
          lastName: api.values.lastName,
          firstNameEn: api.values.firstNameEn,
          lastNameEn: api.values.lastNameEn,
          fatherName: api.values.fatherName,
          street: api.values.street,
          buildingNo: api.values.buildingNo,
          town: api.values.town,
          postalCode: api.values.postalCode,
          mobilePhone: api.values.mobilePhone,
          homePhone: api.values.homePhone,
          workPhone: api.values.workPhone,
          email: api.values.email,
          countryOfBirth: api.values.countryOfBirth,
          yearOfImmigration: api.values.yearOfImmigration,
          immigratedFromCountry: api.values.immigratedFromCountry,
          citizenship: api.values.citizenship,
          additionalCitizenship: api.values.additionalCitizenship,
          maritalStatus: api.values.maritalStatus,
          statusInIsrael: api.values.statusInIsrael,
          spouseIdNumber: api.values.spouseIdNumber,
          spouseFullName: api.values.spouseFullName,
          spouseDateOfBirth: api.values.spouseDateOfBirth,
          spouseOccupation: api.values.spouseOccupation,
          spouseCommencementDate: api.values.spouseCommencementDate,
          spouseWorkplace: api.values.spouseWorkplace,
          children: api.values.children,
          bankName: api.values.bankName,
          bankCity: api.values.bankCity,
          bankNo: api.values.bankNo,
          branchNo: api.values.branchNo,
          accountNo: api.values.accountNo,
          hadDoctorateStudentInLab: api.values.hadDoctorateStudentInLab,
          mentorName: api.values.mentorName,
          department: api.values.department,
          researchTopic: api.values.researchTopic,
          previousDegree: api.values.previousDegree,
          previousInstitution: api.values.previousInstitution,
          graduationYear: api.values.graduationYear,
          signatureDate: api.values.signatureDate,
          statementAccepted: api.values.statementAccepted,
          turnstileToken: turnstileToken || undefined,
        },
      });

      const itemId = res.itemId;

      // 3. Upload attachments via FormData
      if (itemId) {
        const uploadTasks: Promise<any>[] = [];

        // ID document
        if (api.values.idDocumentFile?.dataUrl) {
          const f = dataUrlToFile(
            api.values.idDocumentFile.dataUrl,
            api.values.idDocumentFile.name || 'id_document.pdf',
            api.values.idDocumentFile.type,
          );
          if (f) {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('itemId', itemId);
            fd.append('columnId', 'file_mm7jp3w');
            uploadTasks.push(uploadPostdocAttachment({ data: fd }));
          }
        }

        // Degree certificates
        if (api.values.educationCertFile?.dataUrl) {
          const f = dataUrlToFile(
            api.values.educationCertFile.dataUrl,
            api.values.educationCertFile.name || 'education_certs.pdf',
            api.values.educationCertFile.type,
          );
          if (f) {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('itemId', itemId);
            fd.append('columnId', 'file_mm7jd411');
            uploadTasks.push(uploadPostdocAttachment({ data: fd }));
          }
        }

        // Enrollment approval
        if (api.values.enrollmentAuthFile?.dataUrl) {
          const f = dataUrlToFile(
            api.values.enrollmentAuthFile.dataUrl,
            api.values.enrollmentAuthFile.name || 'admission_approval.pdf',
            api.values.enrollmentAuthFile.type,
          );
          if (f) {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('itemId', itemId);
            fd.append('columnId', 'file_mm7jvvt8');
            uploadTasks.push(uploadPostdocAttachment({ data: fd }));
          }
        }

        // Visa
        if (api.values.studentVisaFile?.dataUrl) {
          const f = dataUrlToFile(
            api.values.studentVisaFile.dataUrl,
            api.values.studentVisaFile.name || 'student_visa.pdf',
            api.values.studentVisaFile.type,
          );
          if (f) {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('itemId', itemId);
            fd.append('columnId', 'file_mm7jxm1q');
            uploadTasks.push(uploadPostdocAttachment({ data: fd }));
          }
        }

        // Digital signature
        if (api.values.signature) {
          const f = dataUrlToFile(api.values.signature, 'signature.png', 'image/png');
          if (f) {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('itemId', itemId);
            fd.append('columnId', 'file_mm7jtf9p');
            uploadTasks.push(uploadPostdocAttachment({ data: fd }));
          }
        }

        // Signed PDF Questionnaire (column 58)
        if (signedPdfFile?.dataUrl) {
          const f = dataUrlToFile(
            signedPdfFile.dataUrl,
            signedPdfFile.name || 'signed_questionnaire.pdf',
            'application/pdf',
          );
          if (f) {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('itemId', itemId);
            fd.append('columnId', 'file_mm7jk0mg');
            uploadTasks.push(uploadPostdocAttachment({ data: fd }));
          }
        }

        // Await all uploads settled
        await Promise.allSettled(uploadTasks);
      }

      setSubmitting(false);
      setReference(res.reference);
      api.clearDraftStorage();
      setMode('submitted');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('בקשת המלגה הוגשה בהצלחה', { description: `מספר אסמכתא: ${res.reference}` });
    } catch (error) {
      console.error('Failed to submit postdoc bursary application', error);
      setSubmitting(false);
      const raw = error instanceof Error ? error.message : '';
      const isPermission = /הרשאת כתיבה|USER_UNAUTHORIZED|403/i.test(raw);
      const message = isPermission
        ? `${raw}\n\nהנתונים שמילאתם נשמרו בדפדפן ולא אבדו — ניתן להגיש שוב לאחר תיקון ההרשאה.`
        : 'חלה שגיאה בעת שמירת הבקשה. נא לנסות שוב.';
      setSubmitError(message);
      toast.error('שליחת הבקשה נכשלה', {
        description: isPermission ? 'נדרשת הרשאת עריכה ללוח היעד' : 'אנא ודאו את הפרטים ונסו שנית.',
      });
    }
  };

  const startNew = () => {
    api.reset();
    setReference('');
    setSubmitError(null);
    setAttemptedReview(false);
    setMode('edit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="mx-auto max-w-[1180px] p-4 sm:p-6 lg:p-8">
      {/* Breadcrumb Navigation */}
      <nav aria-label="נתיב ניווט" className="no-print mb-6 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowRight className="size-3.5" />
          <span>חזרה לקטלוג הטפסים</span>
        </Link>
        <span className="font-mono text-xs text-muted-foreground">טופס מס׳ 01 · מלגת פוסט-דוקטורט</span>
      </nav>

      {mode === 'edit' && (
        <>
          <header className="mb-6 border-b border-[hsl(var(--rule-soft))] pb-6">
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <span>טופס דיגיטלי רשמי</span>
              <span>·</span>
              <span>הפקולטה להנדסה</span>
              {api.values.isContinuingStudent && (
                <span className="border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-emerald-700 dark:text-emerald-300">
                  הצהרת המשך מלגה
                </span>
              )}
            </div>

            <h1 className="mt-2 text-2xl font-bold leading-tight text-foreground sm:text-4xl">
              {api.values.isContinuingStudent
                ? 'הצהרת המשך מלגת פוסט-דוקטורט'
                : 'שאלון ופרטי מועמד/ת לקבלת מלגת פוסט-דוקטורט'}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              {api.values.isContinuingStudent
                ? 'אימות נתונים והצהרה שנתית עבור מועמדים ממשיכים לקבלת מלגה לשנה הבאה.'
                : 'מילוי שאלון פרטים אישיים, מען, חשבון בנק, מעבדת המנחה וצירוף מסמכים למלגת פוסט-דוקטורט.'}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="size-3.5" />
                זמן מילוי משוער: 6 דקות
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="size-3.5 text-emerald-600" />
                טיוטה נשמרת אוטומטית בדפדפן
              </span>
            </div>
          </header>

          <PostdocEditor {...api} />

          <ActionBar
            status={
              api.isValid ? (
                <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="size-4" />
                  כל שדות החובה מולאו — ניתן לעבור לסקירה והגשה / All required fields filled
                </span>
              ) : (
                <span>
                  טופס בקשה למלגת פוסט-דוקטורט · מלאו את הפרטים ולחצו על סקירה והמשך להגשה.
                </span>
              )
            }
            missing={attemptedReview ? api.missingItems : undefined}
            onJump={api.jumpTo}
          >
            {dirty && (
              <BarButton onClick={() => setShowResetDialog(true)} tone="ghost" title="איפוס כל השדות והטיוטה">
                <RotateCcw className="size-3.5" />
                ניקוי טופס (Clear)
              </BarButton>
            )}

            <BarButton onClick={handleReview} tone="primary">
              <Eye className="size-3.5" />
              סקירה והמשך להגשה (Review)
            </BarButton>
          </ActionBar>

          {/* Clear Form Confirmation Dialog */}
          <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
            <DialogContent dir="rtl" className="border-[hsl(var(--rule))] sm:max-w-md">
              <DialogHeader className="text-start">
                <DialogTitle className="flex items-center gap-2 text-start text-base font-bold text-destructive">
                  <Trash2 className="size-5" />
                  איפוס וניקוי נתוני הטופס
                </DialogTitle>
                <DialogDescription className="text-start text-xs leading-relaxed text-muted-foreground">
                  האם ברצונך לנקות את כל השדות, הנספחים והחתימה שמילאת בטופס?
                  <br />
                  פעולה זו תמחק גם את הטיוטה השמורה בדפדפן ולא ניתן יהיה לשחזרה.
                  <br />
                  <span dir="ltr" className="inline-block pt-1 text-left text-[11px] opacity-80">
                    Are you sure you want to clear all form fields, attachments, and saved browser draft?
                  </span>
                </DialogDescription>
              </DialogHeader>

              <DialogFooter className="mt-3 flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowResetDialog(false)}
                  className="inline-flex h-9 items-center justify-center rounded-md border border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-3.5 text-xs font-medium text-foreground hover:bg-[hsl(var(--surface-3))]"
                >
                  ביטול (Cancel)
                </button>
                <button
                  type="button"
                  onClick={handleClearForm}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-destructive bg-destructive px-3.5 text-xs font-semibold text-destructive-foreground hover:bg-destructive/90 shadow-xs"
                >
                  <Trash2 className="size-3.5" />
                  איפוס ומחיקת הטיוטה (Clear All)
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {mode === 'review' && (
        <>
          <header className="mb-6 flex flex-col justify-between gap-4 border-b border-[hsl(var(--rule-soft))] pb-5 sm:flex-row sm:items-center">
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                שלב 2: סקירת הבקשה לפני הגשה
              </span>
              <h1 className="mt-1 text-2xl font-bold text-foreground sm:text-3xl">
                אישור וסקירת טופס הבקשה
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                אנא ודאו את נכונות כל הפרטים לפני ביצוע ההגשה הסופית.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setMode('edit');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="inline-flex h-9 items-center gap-1.5 border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] px-3 text-xs font-medium text-foreground hover:bg-[hsl(var(--surface-3))]"
            >
              <FileEdit className="size-3.5" />
              חזרה לעריכת הפרטים
            </button>
          </header>

          <div ref={documentRef} className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] shadow-xs">
            <PostdocDocument values={api.values} />
          </div>

          <div className="mt-6">
            <TurnstileWidget
              onVerify={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
            />
          </div>

          {submitError ? (
            <div className="mt-4 whitespace-pre-line border border-destructive/40 bg-destructive/10 p-4 text-xs font-medium leading-relaxed text-destructive">
              {submitError}
            </div>
          ) : null}

          <ActionBar
            status={
              turnstileToken ? (
                <span className="flex items-center gap-1.5 font-semibold text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm">
                  <CheckCircle2 className="size-4 shrink-0" />
                  אימות האבטחה הושלם — הטופס מוכן לשליחה.
                </span>
              ) : (
                <span className="flex items-center gap-1.5 font-medium text-amber-800 dark:text-amber-200 bg-amber-500/10 px-2.5 py-1 rounded border border-amber-500/30 text-xs">
                  <AlertCircle className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                  חובה לסמן את ריבוע האימות ("אני לא רובוט") למעלה כדי לשלוח את הטופס
                </span>
              )
            }
          >
            <BarButton
              onClick={() => {
                setMode('edit');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              tone="ghost"
              disabled={submitting}
            >
              <Undo2 className="size-3.5" />
              חזרה לעריכה
            </BarButton>

            <BarButton
              onClick={() => {
                if (!turnstileToken) {
                  const el = document.getElementById('human-verification-widget');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                  toast.error('חובה לסמן את ריבוע האימות "אני לא רובוט" לפני שליחת הטופס.');
                  return;
                }
                submit();
              }}
              tone="primary"
              disabled={submitting}
              className={!turnstileToken ? 'opacity-60 cursor-not-allowed' : ''}
            >
              {submitting ? 'שולח בקשה...' : 'הגשת הבקשה עכשיו (Submit)'}
              {!submitting && <Send className="size-3.5" />}
            </BarButton>
          </ActionBar>
        </>
      )}

      {mode === 'submitted' && (
        <PostdocReceipt values={api.values} reference={reference} onNew={startNew} />
      )}
    </div>
  );
}
